import { InboxOutlined } from '@ant-design/icons';
import { useQueryClient } from '@tanstack/react-query';
import { App, Card, Space, Table, Tabs, Typography, Upload, type TableColumnsType } from 'antd';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

import { useUpdListDocuments } from '@/api/generated/endpoints/upd/upd';
import type { UpdDocumentOut, UpdStatus } from '@/api/generated/model';
import { http } from '@/api/http';
import { useHasRole } from '@/auth/useHasRole';
import { UpdStatusTag } from '@/components/StatusTag';
import { problemMessage } from '@/i18n/problem';
import { invalidatePath } from '@/lib/queryCache';
import { IN_PROGRESS } from '@/lib/updStatus';
import { useIsMobile } from '@/lib/useIsMobile';
import { useTableState } from '@/lib/useTableState';

const DATE = 'DD.MM.YYYY';
const DATETIME = 'DD.MM.YYYY HH:mm';
const POLL_MS = 3_000;

type Tab = 'review' | 'work' | 'all';
const TAB_STATUSES: Record<Tab, UpdStatus[] | null> = {
  review: ['needs_review'],
  work: IN_PROGRESS,
  all: null,
};

/** Загрузка УПД и очередь документов. */
export function UpdListPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const mobile = useIsMobile();
  const table = useTableState<'tab'>({ filters: ['tab'], defaultSort: '-created_at' });
  const tab = (table.filters.tab[0] ?? 'review') as Tab;
  const canUpload = useHasRole('storekeeper');
  const queryClient = useQueryClient();
  const { message } = App.useApp();

  const list = useUpdListDocuments(
    { ...table.query, status: TAB_STATUSES[tab] },
    {
      query: {
        // Пока что-то разбирается — опрашиваем: статусы меняются без перезагрузки страницы.
        refetchInterval: (q) =>
          q.state.data?.items.some((d) => IN_PROGRESS.includes(d.status)) ? POLL_MS : false,
      },
    },
  );

  async function upload(file: File) {
    const form = new FormData();
    form.append('file', file);
    try {
      // Напрямую через http: нужен код ответа — 201 новый документ, 200 тот же файл уже был.
      const res = await http.request<UpdDocumentOut>({
        method: 'POST',
        url: '/api/v1/upd-documents',
        data: form,
      });
      if (res.status === 200) void message.info(t('upd.duplicateFile', { file: file.name }));
      else void message.success(t('upd.uploaded', { file: file.name }));
      table.setFilter('tab', 'all');
      await invalidatePath(queryClient, '/api/v1/upd-documents');
    } catch (e) {
      void message.error(`${file.name}: ${problemMessage(e)}`);
    }
  }

  // Телефон (T-177): документ — одной ячейкой: номер и дата, статус, файл, ошибка.
  const mobileColumns: TableColumnsType<UpdDocumentOut> = [
    {
      key: 'doc',
      title: t('upd.colNumber'),
      render: (_, d) => (
        <Space direction="vertical" size={2} style={{ width: '100%' }}>
          <Space size={8} wrap>
            <Typography.Text strong>
              {d.doc_number ?? '—'}
              {d.doc_date ? ` · ${dayjs(d.doc_date).format(DATE)}` : ''}
            </Typography.Text>
            <UpdStatusTag status={d.status} />
          </Space>
          <Typography.Link style={{ wordBreak: 'break-all' }}>
            {d.original_filename}
          </Typography.Link>
          {d.error && <Typography.Text type="danger">{d.error}</Typography.Text>}
          <Typography.Text type="secondary">
            {dayjs(d.created_at).format(DATETIME)}
            {d.buyer_inn ? ` · ${t('upd.colBuyer')} ${d.buyer_inn}` : ''}
          </Typography.Text>
        </Space>
      ),
    },
  ];

  const columns: TableColumnsType<UpdDocumentOut> = [
    {
      key: 'file',
      title: t('upd.colFile'),
      dataIndex: 'original_filename',
      ellipsis: true,
      render: (name: string) => <a>{name}</a>,
    },
    {
      key: 'number',
      title: t('upd.colNumber'),
      dataIndex: 'doc_number',
      width: 110,
      render: (n: string | null) => n ?? '—',
    },
    {
      key: 'date',
      title: t('upd.colDate'),
      dataIndex: 'doc_date',
      width: 110,
      render: (d: string | null) => (d ? dayjs(d).format(DATE) : '—'),
    },
    {
      key: 'buyer',
      title: t('upd.colBuyer'),
      dataIndex: 'buyer_inn',
      width: 140,
      render: (inn: string | null) => inn ?? '—',
    },
    {
      key: 'status',
      title: t('upd.colStatus'),
      dataIndex: 'status',
      width: 170,
      render: (s: UpdStatus) => <UpdStatusTag status={s} />,
    },
    {
      key: 'error',
      title: t('upd.colError'),
      dataIndex: 'error',
      ellipsis: true,
      render: (e: string | null) =>
        e ? <Typography.Text type="danger">{e}</Typography.Text> : null,
    },
    {
      key: 'created_at',
      title: t('upd.colUploaded'),
      dataIndex: 'created_at',
      width: 140,
      sorter: true,
      sortOrder: table.sortOrderFor('created_at'),
      render: (d: string) => dayjs(d).format(DATETIME),
    },
  ];

  return (
    <Card title={t('upd.title')}>
      {canUpload && (
        <Upload.Dragger
          multiple
          accept=".pdf,.xlsx,.xls,.xml"
          showUploadList={false}
          customRequest={({ file, onSuccess }) => {
            void upload(file as File).then(() => onSuccess?.(null));
          }}
          style={{ marginBottom: 16 }}
        >
          <p className="ant-upload-drag-icon">
            <InboxOutlined />
          </p>
          <p className="ant-upload-text">{t('upd.upload')}</p>
          <p className="ant-upload-hint">{t('upd.uploadHint')}</p>
        </Upload.Dragger>
      )}
      <Tabs
        activeKey={tab}
        onChange={(k) => {
          table.setFilter('tab', k === 'review' ? null : k);
        }}
        items={[
          { key: 'review', label: t('upd.tabReview') },
          { key: 'work', label: t('upd.tabWork') },
          { key: 'all', label: t('upd.tabAll') },
        ]}
      />
      <Table<UpdDocumentOut>
        // Колонки с «…» включают фиксированную раскладку: в узком окне (программа для Windows,
        // 900–1300 px) — прокрутка внутри таблицы, а не колонки шириной в букву; имя файла и ошибка не сжимаются до буквы.
        scroll={mobile ? {} : { x: 1100 }}
        rowKey="id"
        columns={mobile ? mobileColumns : columns}
        dataSource={list.data?.items}
        loading={list.isPending}
        pagination={table.pagination(list.data?.total)}
        onChange={table.onTableChange}
        locale={{ emptyText: t('upd.empty') }}
        onRow={(d) => ({
          onClick: () => {
            void navigate(String(d.id));
          },
          style: { cursor: 'pointer' },
        })}
      />
    </Card>
  );
}
